import { formatListCell, statusPillOf, type ListCellReading } from '../composites/compactRow';
import { csvCell, csvFigure } from '../reports/reports';
import { fill, t } from '../strings';
import { LIST_SELECTION_MAX_ROWS } from './listSelection';
import { LIST_PAGES } from './listPages';
import type { PagedRecord } from './recordPages';

/**
 * 🔒 A list page's Export (decision 10940; T1051): the filtered rows and the visible columns as a CSV,
 * written by the reports' own cell writer, so a cell that opens with a formula mark is quoted the same way.
 */

/** 🔨 The most rows one export writes: the ceiling a filter selection already holds (decision 7780). */
export const LIST_EXPORT_MAX_ROWS = LIST_SELECTION_MAX_ROWS;

/** The route that serves every list's export by its register key, gated as the list's own read (decision 10940). */
export const listExportPath = (recordType: string): string => `/pages/list/${encodeURIComponent(recordType)}/export`;

/** The last row of an export the cap cut, naming the cap and the count it left out (decision 10940). */
export function listExportCapSentence(matched: number): string {
  return fill(t('list.export.capped'), { max: String(LIST_EXPORT_MAX_ROWS), count: String(matched - LIST_EXPORT_MAX_ROWS) });
}

/** The columns an export may carry: a picture and a mark are drawn, never written. */
export function exportableColumns(recordType: PagedRecord) {
  return LIST_PAGES[recordType].columns.filter((column) => column.type !== 'image' && column.type !== 'marks');
}

/** The download's own name, for the list and the day. */
export function listExportFilename(label: string, on: Date): string {
  const stem = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'list';
  return `${stem}-${on.toISOString().slice(0, 10)}.csv`;
}

/** What a cell needs to be written as the list draws it: the viewer's zone, the Organization's date format and the one status resolver (decision 0261). */
export type ListExportReading = Pick<ListCellReading, 'zone' | 'dateFormat' | 'resolveStatus'>;

/**
 * 🔒 One cell as the list draws it (decision 10940, after the batch's review): a status by the label its pill
 * reads, a calendar day as the day, an instant on the viewer's own day, and a link by its text, through the
 * list's own formatter. A money and a count column stay figures a spreadsheet sums, money in decimal dollars (decision 0564).
 */
function exportCell(column: ReturnType<typeof exportableColumns>[number], row: Readonly<Record<string, unknown>>, reading: ListExportReading): string {
  const value = row[column.key];
  if (column.type === 'currency') return csvFigure(typeof value === 'number' ? value : value === null || value === undefined ? null : String(value), 'money');
  if (column.type === 'number') return csvCell(typeof value === 'number' ? value : value === null || value === undefined ? null : String(value));
  if (column.type === 'status') return csvCell(statusPillOf(column, row, reading.resolveStatus)?.label ?? null);
  // The currency code is never read here: a money column took the branch above.
  return csvCell(formatListCell(column, value, { ...reading, currencyCode: 'USD' }));
}

/**
 * The CSV for the columns asked, in the order the list draws them, each cell as the list draws it. The rows
 * keep the list read's order. Rows past the cap are cut and the file's last row says so, never silently.
 */
export function listExportCsv(recordType: PagedRecord, keys: readonly string[], rows: readonly Record<string, unknown>[], reading: ListExportReading): string {
  const available = exportableColumns(recordType);
  const columns = (keys.length > 0 ? keys.flatMap((key) => available.filter((column) => column.key === key)) : available.filter((column) => !column.offByDefault));
  const lines = [columns.map((column) => csvCell(column.label)).join(',')];
  for (const row of rows.slice(0, LIST_EXPORT_MAX_ROWS)) lines.push(columns.map((column) => exportCell(column, row, reading)).join(','));
  // T3644: the sentence sits in the first cell and the rest stay empty, so the row has the file's width and a sum over the first column is not fed prose by a shifted cell.
  if (rows.length > LIST_EXPORT_MAX_ROWS) lines.push([csvCell(listExportCapSentence(rows.length)), ...columns.slice(1).map(() => '')].join(','));
  return `${lines.join('\n')}\n`;
}
